"use client";

import { Link, useLocation, useNavigate } from "react-router";
import { useAuth } from "../../contexts/authContext";
import { Crown, Menu } from "lucide-react";
import { EXTERNAL_LINKS, EXTERNAL_LINK_PROPS } from "../../utils/externalLinks";
import NotificationBell from "../notifications/NotificationBell";

function NavBar() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const isDashboard =
    location.pathname.startsWith("/admin") ||
    location.pathname.startsWith("/lecturer") ||
    location.pathname.startsWith("/student");

  const isPublicPage = !isDashboard;

  const handleLogout = () => {
    navigate("/", { replace: true });
    logout();
    document.activeElement?.blur();
  };

  const getNotificationsLink = () => {
    if (user?.is_admin) return "/admin/notifications";
    if (user?.user_type) return `/${user.user_type}/notifications`;
    return "/notifications";
  };

  const PublicMenu = () => (
    <div className="hidden lg:flex items-center gap-6">
      <a href={EXTERNAL_LINKS.about} {...EXTERNAL_LINK_PROPS} className="font-medium hover:text-primary text-gray01 opacity-80">
        About
      </a>
      <a href={EXTERNAL_LINKS.pricing} {...EXTERNAL_LINK_PROPS} className="font-medium hover:text-primary text-gray01 opacity-80">
        Pricing
      </a>
      <div className="dropdown dropdown-hover">
        <label
          tabIndex={0}
          className="cursor-pointer text-gray01 opacity-80 font-medium hover:text-primary"
        >
          Legal
        </label>
        <ul tabIndex={0} className="dropdown-content menu p-2 shadow bg-base-100 rounded-box w-52">
          <li className="hover:text-primary">
            <a href={EXTERNAL_LINKS.privacyPolicy} {...EXTERNAL_LINK_PROPS}>Privacy Policy</a>
          </li>
          <li className="hover:text-primary">
            <a href={EXTERNAL_LINKS.termsAndConditions} {...EXTERNAL_LINK_PROPS}>Terms & Conditions</a>
          </li>
        </ul>
      </div>
      <a href={EXTERNAL_LINKS.contact} {...EXTERNAL_LINK_PROPS} className="font-medium hover:text-primary text-gray01 opacity-80">
        Contact
      </a>
    </div>
  );

  const UserInfo = () => (
    <div>
      <p className="text-sm capitalize">
        {user.first_name} {user.last_name}
      </p>
      <p className="text-xs opacity-66 capitalize">{user.is_admin ? "Admin" : user.user_type}</p>
    </div>
  );

  const getSubscriptionRingGradient = () => {
    if (!user?.has_active_subscription) return "";
    if (user.subscription_type === "enterprise") {
      return "bg-gradient-to-tr from-purple-600 via-indigo-500 to-violet-400";
    }
    // Premium / default active subscription plan
    return "bg-gradient-to-tr from-amber-500 via-yellow-400 to-orange-400";
  };

  const UserDropdown = () => (
    <div className="dropdown dropdown-end">
      <div tabIndex={0} role="button" className="btn btn-ghost btn-circle avatar">
        {user?.has_active_subscription ? (
          <div className={`w-10 h-10 rounded-full p-[2.5px] ${getSubscriptionRingGradient()} transition-transform duration-200 hover:scale-105 shadow-sm`}>
            <div className="w-full h-full rounded-full p-[2px] bg-base-100 flex items-center justify-center">
              <img
                alt="user avatar"
                className="w-full h-full rounded-full object-cover"
                src={user.profile_pic || "https://img.daisyui.com/images/stock/photo-1534528741775-53994a69daeb.webp"}
              />
            </div>
          </div>
        ) : (
          <div className="w-10 rounded-full">
            <img
              alt="user avatar"
              src={user.profile_pic || "https://img.daisyui.com/images/stock/photo-1534528741775-53994a69daeb.webp"}
            />
          </div>
        )}
      </div>
      <div
        tabIndex={0}
        className="dropdown-content bg-base-100 rounded-xl z-20 mt-3 w-56 p-2 shadow-xl border border-base-200"
      >
        {/* Header Profile & Plan Card */}
        <div className="px-3 py-2.5 mb-1.5 bg-base-200/50 rounded-lg">
          <div className="flex items-center justify-between gap-2">
            <p className="text-sm font-semibold text-gray01 truncate capitalize">
              {user.first_name} {user.last_name}
            </p>
            {user.has_active_subscription && (
              <span
                className={`px-2 py-0.5 text-white text-[10px] uppercase font-bold tracking-wider rounded-full shrink-0 ${user.subscription_type === "enterprise" ? "bg-purple-600" : "bg-amber-500"
                  }`}
              >
                {user.subscription_type}
              </span>
            )}
          </div>
          {user.email && (
            <p className="text-xs text-gray-500 truncate mt-0.5">{user.email}</p>
          )}
        </div>

        <ul className="menu menu-sm p-0">
          {user?.is_admin && (
            <li>
              <Link to="/admin">Admin dashboard</Link>
            </li>
          )}
          {user?.user_type === "lecturer" && (
            <li>
              <Link to="/lecturer/dashboard">Dashboard</Link>
            </li>
          )}
          {(user?.user_type === "student" || user?.user_type === "preper") && (
            <li>
              <Link to={`/${user.user_type}/dashboard`}>Dashboard</Link>
            </li>
          )}
          <li>
            <Link to={getNotificationsLink()}>Notifications</Link>
          </li>
          <li>
            <Link to={`/${user.user_type}/subscription`}>Subscription</Link>
          </li>
          <div className="divider my-1"></div>
          <li>
            <a onClick={handleLogout} className="text-error hover:bg-error/10">
              Logout
            </a>
          </li>
        </ul>
      </div>
    </div>
  );

  const AuthenticatedMenu = () => (
    <div className="hidden lg:flex items-center gap-3">
      {!user.has_active_subscription && (
        <Link to={`/${user.user_type}/subscription`}>
          <button className="btn bg-yellow01 text-yellow02 flex items-center gap-1">
            <Crown size={16} />
            <span>Upgrade</span>
          </button>
        </Link>
      )}
      <NotificationBell />
      <UserDropdown />
      {user && <UserInfo />}
    </div>
  );

  const MobileMenuItems = () => {
    if (!user) {
      return (
        <>
          <li>
            <a href={EXTERNAL_LINKS.about} {...EXTERNAL_LINK_PROPS}>About</a>
          </li>
          <li>
            <a href={EXTERNAL_LINKS.pricing} {...EXTERNAL_LINK_PROPS}>Pricing</a>
          </li>
          <li>
            <a href={EXTERNAL_LINKS.privacyPolicy} {...EXTERNAL_LINK_PROPS}>Privacy Policy</a>
          </li>
          <li>
            <a href={EXTERNAL_LINKS.termsAndConditions} {...EXTERNAL_LINK_PROPS}>Terms & Conditions</a>
          </li>
          <li>
            <a href={EXTERNAL_LINKS.contact} {...EXTERNAL_LINK_PROPS}>Contact</a>
          </li>
          <li>
            <Link to="/signup" className="btn btn-outline btn-primary w-full mt-2">
              Signup
            </Link>
          </li>
          <li>
            <Link to="/login" className="btn btn-primary w-full text-white">
              Login
            </Link>
          </li>
        </>
      );
    }

    return (
      <>
        {user?.is_admin && (
          <li>
            <Link to="/admin">Admin</Link>
          </li>
        )}
        {user?.user_type === "lecturer" && (
          <li>
            <Link to="/lecturer/dashboard">Dashboard</Link>
          </li>
        )}
        {(user?.user_type === "student" || user?.user_type === "preper") && (
          <li>
            <Link to={`/${user.user_type}/dashboard`}>Dashboard</Link>
          </li>
        )}
        <li>
          <Link to={getNotificationsLink()}>Notifications</Link>
        </li>
        {!user.has_active_subscription && (
          <li>
            <Link
              to="/subscription"
              className="flex items-center gap-1 text-violet-600 font-medium"
            >
              <Crown size={14} /> Upgrade to Premium
            </Link>
          </li>
        )}
        <li>
          <a onClick={handleLogout}>Logout</a>
        </li>
      </>
    );
  };

  const MobileMenu = () => (
    <div className="lg:hidden z-50">
      <div className="dropdown dropdown-end">
        <label tabIndex={0} className="btn btn-ghost">
          <Menu size={22} />
        </label>
        <ul
          tabIndex={0}
          className="menu menu-lg dropdown-content mt-3 p-2 shadow bg-base-100 rounded-box w-72"
        >
          <MobileMenuItems />
        </ul>
      </div>
    </div>
  );

  const Logo = () => (
    <a href='/'>
      <img src="/logo.png" alt="Examsprep logo" className="w-3/5 md:w-52" />
    </a>
  );

  const DashboardTitle = () => (
    <h4 className="capitalize">{user.is_admin ? "Admin" : user.user_type} Dashboard</h4>
  );

  const DashboardLayout = () => (
    <div className="h-24 md:h-16 flex items-center">
      <div className="ml-7">
        <Logo />
        <h4 className="capitalize text-xs w-full mb-2 block md:hidden">
          {user.is_admin ? "Admin" : user.user_type} Dashboard
        </h4>
      </div>
      <div className="flex items-center justify-between w-full md:mx-12 lg:max-w-7xl 2xl:mx-auto">
        <h4 className="capitalize hidden md:block md:ml-16 2xl:ml-7">
          {user.is_admin ? "Admin" : user.user_type} Dashboard
        </h4>
        <AuthenticatedMenu />
      </div>
      <div className="flex items-center gap-1 lg:hidden mr-2">
        {user && <NotificationBell />}
        <MobileMenu />
      </div>
    </div>
  );

  const PublicLayout = () => (
    <div className="layout-width justify-between h-16 flex items-center">
      <div>
        <Logo />
      </div>

      {user && isDashboard && <DashboardTitle />}

      {isPublicPage && <PublicMenu />}

      {!user && isPublicPage && (
        <div className="hidden lg:flex items-center gap-2">
          <Link to="/signup" className="btn btn-outline btn-primary rounded-full w-24">
            Signup
          </Link>
          <Link to="/login" className="btn btn-primary rounded-full w-24">
            Login
          </Link>
        </div>
      )}

      {user && <AuthenticatedMenu />}

      <div className="flex items-center gap-1 lg:hidden">
        {user && <NotificationBell />}
        <MobileMenu />
      </div>
    </div>
  );

  return (
    <div className="bg-base-100 shadow-sm">
      {user && isDashboard ? <DashboardLayout /> : <PublicLayout />}
    </div>
  );
}

export default NavBar;
